import {
  Timestamp,
  collection,
  documentId,
  doc,
  increment,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

import { auth, db } from "../../../firebaseConfig";
import { calculateBusinessRoundedHoursFromMinutes } from "../../../utils/sessionHours";
import {
  STUDENT_INACTIVITY_PAUSE_DAYS,
  applyStudentAutoStatusTransitions,
  formatStudentDateKey,
  normalizeStudentStatus,
  parseStudentLifecycleDate,
} from "../../../utils/studentLifecycle";

export const SESSION_LOGS_COL = "session_logs";

export const SESSION_TYPE_OPTIONS = [
  "School Help",
  "SAT",
  "PSAT",
  "ACT",
  "Skill Building",
  "Diagnostic Review",
  "Homework Support",
  "Other",
];

export const HOMEWORK_STATUS_OPTIONS = [
  "Completed",
  "Partially Done",
  "Not Done",
  "Not Assigned",
];

export const STUDENT_FLAG_OPTIONS = ["On Track", "Needs Attention", "At Risk"];

const STAR_FIELDS = ["effort", "motivation", "behavior", "focus", "confidence"];

export function toDateMaybe(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value === "object" && "seconds" in value) return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function toDateKeyLocal(value) {
  const d = toDateMaybe(value);
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function toDateInputValue(value) {
  return toDateKeyLocal(value);
}

export function toTimeInputValue(value) {
  const d = toDateMaybe(value);
  if (!d) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function toStringSafe(value) {
  return (value == null ? "" : String(value)).trim();
}

function toIntMaybe(value) {
  if (value == null || value === "") return null;
  const n = Number.parseInt(String(value), 10);
  return Number.isFinite(n) ? n : null;
}

function toRating(value) {
  const n = toIntMaybe(value);
  if (!Number.isFinite(n)) return null;
  if (n < 1 || n > 5) return null;
  return n;
}

function toTimestampMaybe(value) {
  const d = toDateMaybe(value);
  return d ? Timestamp.fromDate(d) : null;
}

function normalizeText(value) {
  return toStringSafe(value).replace(/\s+/g, " ").trim();
}

function normalizeParagraph(value) {
  return toStringSafe(value).replace(/\r\n/g, "\n").trim();
}

export function calculateRoundedUsedHours(start, end) {
  const s = toDateMaybe(start);
  const e = toDateMaybe(end);
  if (!s || !e) return 0;

  const diffMin = Math.ceil((e.getTime() - s.getTime()) / 60000);
  if (diffMin <= 0) return 0;
  return calculateBusinessRoundedHoursFromMinutes(diffMin);
}

function shouldUpdateLastSessionDate(nextDateKey, currentDateKey) {
  if (!nextDateKey) return false;
  if (!currentDateKey) return true;
  const nextMs = parseStudentLifecycleDate(nextDateKey)?.getTime() || 0;
  const currentMs = parseStudentLifecycleDate(currentDateKey)?.getTime() || 0;
  return nextMs > currentMs;
}

async function updateStudentHoursAndLifecycle({
  studentId,
  deltaHours = 0,
  nextSessionEnd = null,
  nextSessionDateKey = "",
  now = Timestamp.now(),
}) {
  const sid = toStringSafe(studentId);
  const delta = Number(deltaHours);
  if (!sid) return;

  const hasDelta = Number.isFinite(delta) && Math.abs(delta) > 0.0001;
  const nextDateKey = formatStudentDateKey(nextSessionDateKey) || formatStudentDateKey(nextSessionEnd);

  if (!hasDelta && !nextDateKey) return;

  const studentRef = doc(db, "students", sid);
  const studentSnap = await getDoc(studentRef);
  if (!studentSnap.exists()) return;

  const student = studentSnap.data() || {};
  const patch = {
    updatedAt: now,
  };

  if (hasDelta) {
    patch.totalSessionHours = increment(delta);
    patch.totalSessionHoursUpdatedAt = now;
  }

  const currentLastDate = formatStudentDateKey(student?.endDate);
  const mergedLastDate = shouldUpdateLastSessionDate(nextDateKey, currentLastDate)
    ? nextDateKey
    : currentLastDate;
  if (mergedLastDate && String(student?.endDate || "") !== mergedLastDate) {
    patch.endDate = mergedLastDate;
  }

  const currentStatus = normalizeStudentStatus(student?.status);
  const currentTotalHours = Number(student?.totalSessionHours || 0);
  const projectedHours = Number.isFinite(currentTotalHours + delta) ? currentTotalHours + delta : currentTotalHours;
  const nextStatus = applyStudentAutoStatusTransitions({
    currentStatus,
    hasAnySession: projectedHours > 0 || !!mergedLastDate,
    lastSessionDate: mergedLastDate,
    inactivityDays: STUDENT_INACTIVITY_PAUSE_DAYS,
    now: now?.toDate?.() || new Date(),
  });
  if (String(student?.status || "") !== nextStatus) {
    patch.status = nextStatus;
  }

  if (Object.keys(patch).length > 1) {
    await updateDoc(studentRef, patch);
  }
}

export function calculateAccuracyPercent(attemptedRaw, wrongRaw) {
  const attempted = toIntMaybe(attemptedRaw);
  const wrong = toIntMaybe(wrongRaw);

  if (!Number.isFinite(attempted) || attempted <= 0) return null;

  const wrongCount = Number.isFinite(wrong) ? Math.max(0, wrong) : 0;
  const correct = Math.max(0, attempted - wrongCount);
  const pct = Math.round((correct / attempted) * 100);
  return Math.max(0, Math.min(100, pct));
}

export function normalizeHomeworkStatus(raw) {
  const value = toStringSafe(raw).toLowerCase();
  if (!value) return "";

  if (value === "completed" || value === "yes") return "Completed";
  if (value.includes("partial")) return "Partially Done";
  if (value === "not done" || value === "no") return "Not Done";
  if (value === "not assigned" || value === "n/a") return "Not Assigned";

  return "";
}

function inferStudentFlag({ ratings, explicitFlag }) {
  const normalizedFlag = toStringSafe(explicitFlag);
  if (STUDENT_FLAG_OPTIONS.includes(normalizedFlag)) return normalizedFlag;

  const stars = STAR_FIELDS.map((key) => toRating(ratings?.[key])).filter((v) => Number.isFinite(v));
  if (!stars.length) return "";

  const avg = stars.reduce((sum, v) => sum + v, 0) / stars.length;
  if (avg <= 2) return "At Risk";
  if (avg < 3.5) return "Needs Attention";
  return "On Track";
}

export function buildLogDefaultsFromSession(session) {
  const start = toDateMaybe(session?.start || session?.sessionStart);
  const end = toDateMaybe(session?.end || session?.sessionEnd);

  return {
    sessionType: "",
    topicCovered: "",

    homeworkStatus: "",
    homeworkComments: "",

    materialUsed: "",
    materials: [],
    questionsAttempted: "",
    questionsWrong: "",
    lessonActivity: "",
    learningInsight: "",
    nextFocus: "",
    homeworkGiven: "",

    effort: "",
    motivation: "",
    behavior: "",
    focus: "",
    confidence: "",
    studentFlag: "",

    aiHomeworkAssigned: "",
    aiSessionSummary: "",
    aiNextSessionPlan: "",
    aiRiskAlert: "",

    auto: {
      date: toDateInputValue(start),
      startTime: toTimeInputValue(start),
      endTime: toTimeInputValue(end),
      usedHours: calculateRoundedUsedHours(start, end),
      tutorName: toStringSafe(session?.tutorName),
      studentName: toStringSafe(session?.studentName),
      subject: toStringSafe(session?.subject),
      shortAdminNote: toStringSafe(session?.note),
    },
  };
}

function mergeLogIntoForm(defaults, logData = {}) {
  const ratings = logData.ratings || {};
  const ai = logData.ai || {};

  return {
    ...defaults,
    sessionType: toStringSafe(logData.sessionType),
    topicCovered: toStringSafe(logData.topicCovered),

    homeworkStatus: toStringSafe(logData.homeworkStatus),
    homeworkComments: toStringSafe(logData.homeworkComments),

    materialUsed: toStringSafe(logData.materialUsed),
    // Structured, clickable material references (newer logs). Older logs only
    // have the newline-joined `materialUsed` string and stay as plain text.
    materials: Array.isArray(logData.materials) ? logData.materials : [],
    questionsAttempted: logData.questionsAttempted == null ? "" : String(logData.questionsAttempted),
    questionsWrong: logData.questionsWrong == null ? "" : String(logData.questionsWrong),

    lessonActivity: toStringSafe(logData.lessonActivity),
    learningInsight: toStringSafe(logData.learningInsight),
    nextFocus: toStringSafe(logData.nextFocus),
    homeworkGiven: toStringSafe(logData.homeworkGiven),

    effort: ratings.effort == null ? "" : String(ratings.effort),
    motivation: ratings.motivation == null ? "" : String(ratings.motivation),
    behavior: ratings.behavior == null ? "" : String(ratings.behavior),
    focus: ratings.focus == null ? "" : String(ratings.focus),
    confidence: ratings.confidence == null ? "" : String(ratings.confidence),
    studentFlag: toStringSafe(logData.studentFlag),

    aiHomeworkAssigned: toStringSafe(ai.homeworkAssigned),
    aiSessionSummary: toStringSafe(ai.sessionSummary),
    aiNextSessionPlan: toStringSafe(ai.nextSessionPlan),
    aiRiskAlert: toStringSafe(ai.riskAlert),

    // Read-only attribution surfaced in the detail view when an admin typed in
    // the log on the tutor's behalf. Intentionally not persisted on save —
    // `buildSessionLogPayload` writes an explicit field allowlist.
    entryMeta: {
      enteredByAdmin: logData.enteredByAdmin === true,
      enteredByAdminName: toStringSafe(logData.enteredByAdminName),
      createdByUid: toStringSafe(logData.createdByUid),
      createdAt: logData.createdAt || null,
    },
  };
}

export async function fetchSessionLogBySessionId(sessionId) {
  if (!sessionId) return null;
  const ref = doc(db, SESSION_LOGS_COL, sessionId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function fetchSessionLogsBySessionIds(sessionIds = []) {
  const ids = [...new Set((sessionIds || []).filter(Boolean))];
  if (!ids.length) return {};

  const out = {};
  for (let i = 0; i < ids.length; i += 30) {
    const chunk = ids.slice(i, i + 30);
    const q = query(collection(db, SESSION_LOGS_COL), where(documentId(), "in", chunk));
    const snap = await getDocs(q);
    snap.docs.forEach((d) => {
      const row = { id: d.id, ...d.data() };
      // Draft autosaves should never count as "submitted" for schedule checkmarks.
      if (row.isDraft === true) return;
      out[d.id] = row;
    });
  }
  return out;
}

/**
 * Resolve the signed-in user's uid, display name and role.
 *
 * Used to decide whether the session-log form is being filled in by an admin on
 * a tutor's behalf. The role is read from the `users/{uid}` document (the same
 * source the app's role routing trusts) so a crafted `?adminEntry=1` URL alone
 * can never grant admin attribution to a tutor.
 */
export async function fetchCurrentUserIdentity() {
  const user = auth.currentUser;
  if (!user) return { uid: "", name: "", role: "" };

  const fallbackName = toStringSafe(user.displayName) || toStringSafe(user.email);
  try {
    const snap = await getDoc(doc(db, "users", user.uid));
    if (snap.exists()) {
      const data = snap.data() || {};
      return {
        uid: user.uid,
        name: toStringSafe(data.name) || fallbackName,
        role: String(data.role || "").toLowerCase(),
      };
    }
  } catch (error) {
    console.warn("Failed to resolve current user identity for session log.", error);
  }

  return { uid: user.uid, name: fallbackName, role: "" };
}

export async function loadSessionLogForm(session) {
  const defaults = buildLogDefaultsFromSession(session);
  if (!session?.id) return defaults;

  const existing = await fetchSessionLogBySessionId(session.id);
  if (!existing) return defaults;

  return mergeLogIntoForm(defaults, existing);
}

function buildRatings(form = {}) {
  return STAR_FIELDS.reduce((acc, key) => {
    acc[key] = toRating(form[key]);
    return acc;
  }, {});
}

// Normalize the structured materials array for Firestore (no undefined values;
// every reference field is a string). Items without a label are dropped.
function sanitizeMaterials(materials) {
  if (!Array.isArray(materials)) return [];
  return materials
    .map((m) => {
      if (typeof m === "string") {
        const label = m.trim();
        return label ? { label, url: "", type: "text" } : null;
      }
      const label = toStringSafe(m?.label || m?.title).trim();
      if (!label) return null;
      const url = toStringSafe(m?.url).trim();
      return {
        label,
        url,
        type: toStringSafe(m?.type) || (url ? "link" : "text"),
        librarySlug: toStringSafe(m?.librarySlug),
        libraryId: toStringSafe(m?.libraryId),
        categoryId: toStringSafe(m?.categoryId),
        textbookId: toStringSafe(m?.textbookId),
        resourceId: toStringSafe(m?.resourceId),
      };
    })
    .filter(Boolean);
}

export function buildSessionLogPayload({
  session,
  form,
  currentUserUid,
  createdAt,
  createdByUid,
  source = "tutor_form",
  enteredByAdmin = false,
  enteredByAdminName = "",
  enteredByAdminUid = "",
}) {
  const start = toDateMaybe(session?.start);
  const end = toDateMaybe(session?.end);

  const questionsAttempted = toIntMaybe(form?.questionsAttempted);
  const questionsWrong = toIntMaybe(form?.questionsWrong);
  const accuracyPercent = calculateAccuracyPercent(questionsAttempted, questionsWrong);

  const ratings = buildRatings(form);
  const studentFlag = inferStudentFlag({ ratings, explicitFlag: form?.studentFlag });

  const now = Timestamp.now();

  return {
    sessionId: session?.id || null,
    sessionStart: toTimestampMaybe(start),
    sessionEnd: toTimestampMaybe(end),
    sessionDateKey: toDateKeyLocal(start),

    tutorUid: toStringSafe(session?.tutorUid) || toStringSafe(currentUserUid) || null,
    tutorName: normalizeText(session?.tutorName || form?.auto?.tutorName),
    studentId: toStringSafe(session?.studentId) || null,
    studentName: normalizeText(session?.studentName || form?.auto?.studentName),
    studentNameLower: normalizeText(session?.studentName || form?.auto?.studentName).toLowerCase(),
    subject: normalizeText(session?.subject || form?.auto?.subject),
    shortAdminNote: normalizeParagraph(session?.note || form?.auto?.shortAdminNote),

    usedHours: calculateRoundedUsedHours(start, end),

    sessionType: normalizeText(form?.sessionType),
    topicCovered: normalizeText(form?.topicCovered),

    homeworkStatus: normalizeHomeworkStatus(form?.homeworkStatus),
    homeworkComments: normalizeParagraph(form?.homeworkComments),

    materialUsed: normalizeParagraph(form?.materialUsed),
    materials: sanitizeMaterials(form?.materials),
    questionsAttempted,
    questionsWrong,
    accuracyPercent,

    lessonActivity: normalizeParagraph(form?.lessonActivity),
    learningInsight: normalizeParagraph(form?.learningInsight),
    nextFocus: normalizeParagraph(form?.nextFocus),
    homeworkGiven: normalizeParagraph(form?.homeworkGiven),

    ratings,
    studentFlag,

    ai: {
      homeworkAssigned: normalizeParagraph(form?.aiHomeworkAssigned),
      sessionSummary: normalizeParagraph(form?.aiSessionSummary),
      nextSessionPlan: normalizeParagraph(form?.aiNextSessionPlan),
      riskAlert: normalizeParagraph(form?.aiRiskAlert),
    },

    source,
    // Attribution: the log always belongs to the scheduled session's tutor, but
    // an admin may have typed it in on the tutor's behalf. These fields record
    // who actually entered the data so the two are never confused.
    enteredByAdmin: !!enteredByAdmin,
    enteredByAdminName: enteredByAdmin ? normalizeText(enteredByAdminName) : "",
    enteredByAdminUid: enteredByAdmin ? toStringSafe(enteredByAdminUid) || null : null,
    importedLegacy: false,
    updatedAt: now,
    updatedByUid: toStringSafe(currentUserUid) || null,
    createdAt: createdAt || now,
    createdByUid: createdByUid || toStringSafe(currentUserUid) || null,
  };
}

export async function upsertSessionLogForSession({
  session,
  form,
  currentUserUid,
  source = "tutor_form",
  enteredByAdmin = false,
  enteredByAdminName = "",
  enteredByAdminUid = "",
}) {
  if (!session?.id) {
    throw new Error("Cannot save session log without a session id.");
  }

  const ref = doc(db, SESSION_LOGS_COL, session.id);
  const existing = await getDoc(ref);
  const existingData = existing.exists() ? existing.data() : null;

  const payload = buildSessionLogPayload({
    session,
    form,
    currentUserUid,
    createdAt: existingData?.createdAt,
    createdByUid: existingData?.createdByUid,
    source,
    enteredByAdmin,
    enteredByAdminName,
    enteredByAdminUid,
  });

  // A log is considered a draft ONLY if it is newly created via autosave 
  // or if it was already explicitly marked as a draft. Past official logs are NOT drafts.
  // Any `*_autosave` source (tutor or admin) is a silent background save.
  const isAutosave = String(source || "").endsWith("_autosave");
  const wasDraft = existingData ? (existingData.isDraft === true) : true;
  payload.isDraft = isAutosave && wasDraft;

  // Preserve original entry attribution: `enteredByAdmin*` records who FIRST
  // authored the log, so a later edit by a different admin never rewrites
  // history. `updatedByUid` still tracks whoever touched it last.
  if (existingData) {
    payload.enteredByAdmin = existingData.enteredByAdmin === true;
    payload.enteredByAdminName = toStringSafe(existingData.enteredByAdminName);
    payload.enteredByAdminUid = toStringSafe(existingData.enteredByAdminUid) || null;
  }

  await setDoc(ref, payload, { merge: true });

  if (isAutosave) {
    // Autosaves are silent background updates. We strictly DO NOT apply billing hours
    // or mark auto-attendance until the tutor explicitly hits "Submit Session Log".
    return payload;
  }

  const studentId = String(payload?.studentId || "").trim();
  const prevStudentId = String(existingData?.studentId || "").trim();
  
  // If the existing record was just a draft, its hours were never added to the student's total.
  // Therefore, we calculate the delta starting from 0 to apply the full session amount.
  const prevUsed = (existingData?.isDraft === true) ? 0 : Number(existingData?.usedHours || 0);
  const nextUsed = Number(payload?.usedHours || 0);
  const sessionEndForLifecycle = toDateMaybe(payload?.sessionEnd || payload?.sessionStart);

  try {
    if (studentId && prevStudentId && studentId !== prevStudentId) {
      await updateStudentHoursAndLifecycle({
        studentId: prevStudentId,
        deltaHours: -prevUsed,
      });
      await updateStudentHoursAndLifecycle({
        studentId,
        deltaHours: nextUsed,
        nextSessionEnd: sessionEndForLifecycle,
        nextSessionDateKey: payload?.sessionDateKey || "",
      });
    } else if (studentId) {
      const delta = Number.isFinite(nextUsed - prevUsed) ? nextUsed - prevUsed : 0;
      await updateStudentHoursAndLifecycle({
        studentId,
        deltaHours: delta,
        nextSessionEnd: sessionEndForLifecycle,
        nextSessionDateKey: payload?.sessionDateKey || "",
      });
    }
  } catch (studentHoursError) {
    console.warn("Failed to update student totalSessionHours from session log save.", studentHoursError);
  }

  try {
    await updateDoc(doc(db, "scheduled_sessions", session.id), {
      status: "Present",
      attendanceMarkedAt: Timestamp.now(),
      attendanceMarkedBy: "session_log",
      updatedAt: Timestamp.now(),
    });
  } catch (attendanceError) {
    console.warn("Failed to mark scheduled session as Present after session log save.", attendanceError);
  }

  return payload;
}

export async function fetchRecentSessionLogs(maxRows = 300, options = {}) {
  const tutorUid = toStringSafe(options?.tutorUid);
  const constraints = [];

  if (tutorUid) {
    // If it's a tutor, filter by their ID but DO NOT order by date.
    // This instantly bypasses the Firebase Composite Index error!
    // (The React frontend already sorts these perfectly in memory anyway).
    constraints.push(where("tutorUid", "==", tutorUid));
  } else {
    // Admins don't filter by tutor, so they can still safely order by date.
    constraints.push(orderBy("sessionStart", "desc"));
  }

  constraints.push(limit(maxRows));

  const q = query(collection(db, SESSION_LOGS_COL), ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fetchSessionLogsByDateRange({
  startDate,
  endDate,
  maxRows = 600,
} = {}) {
  const start = toDateMaybe(startDate);
  const end = toDateMaybe(endDate);
  if (!start || !end || end <= start) return [];

  const q = query(
    collection(db, SESSION_LOGS_COL),
    where("sessionStart", ">=", Timestamp.fromDate(start)),
    where("sessionStart", "<", Timestamp.fromDate(end)),
    orderBy("sessionStart", "desc"),
    limit(Math.max(1, Number(maxRows) || 600))
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fetchOldestSessionLogStart() {
  const q = query(
    collection(db, SESSION_LOGS_COL),
    orderBy("sessionStart", "asc"),
    limit(1)
  );
  const snap = await getDocs(q);
  if (!snap.docs.length) return null;
  return toDateMaybe(snap.docs[0]?.data()?.sessionStart);
}

export async function fetchSessionLogsForStudent(studentName, { maxRows = 2500 } = {}) {
  const normalized = normalizeText(studentName).toLowerCase();
  if (!normalized) return [];

  const rows = [];
  const seen = new Set();

  const byLowerQ = query(
    collection(db, SESSION_LOGS_COL),
    where("studentNameLower", "==", normalized),
    limit(Math.max(1, Number(maxRows) || 2500))
  );
  const byLowerSnap = await getDocs(byLowerQ);
  byLowerSnap.docs.forEach((d) => {
    if (seen.has(d.id)) return;
    seen.add(d.id);
    rows.push({ id: d.id, ...d.data() });
  });

  // Backward compatibility for older rows that may not have studentNameLower.
  const exactName = normalizeText(studentName);
  if (rows.length < maxRows && exactName) {
    const byNameQ = query(
      collection(db, SESSION_LOGS_COL),
      where("studentName", "==", exactName),
      limit(Math.max(1, Number(maxRows) || 2500))
    );
    const byNameSnap = await getDocs(byNameQ);
    byNameSnap.docs.forEach((d) => {
      if (seen.has(d.id)) return;
      seen.add(d.id);
      rows.push({ id: d.id, ...d.data() });
    });
  }

  return rows;
}

function parseLegacyDate(value) {
  const raw = toStringSafe(value);
  if (!raw) return null;

  const parts = raw.split("/").map((p) => p.trim());
  if (parts.length !== 3) return null;

  const month = Number(parts[0]);
  const day = Number(parts[1]);
  const year = Number(parts[2]);
  if (![month, day, year].every(Number.isFinite)) return null;

  const d = new Date(year, month - 1, day);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

function parseLegacyTimeToMinutes(value) {
  const raw = toStringSafe(value);
  if (!raw) return null;

  const match = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
  if (!match) return null;

  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const ampm = match[3] ? match[3].toUpperCase() : null;

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;

  if (ampm) {
    if (ampm === "PM" && hour < 12) hour += 12;
    if (ampm === "AM" && hour === 12) hour = 0;
  }

  return hour * 60 + minute;
}

function applyMinutesToDate(base, minutes) {
  if (!base || !Number.isFinite(minutes)) return null;
  const d = new Date(base);
  d.setHours(0, 0, 0, 0);
  d.setMinutes(minutes);
  return d;
}

function numberOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeLegacyHomeworkStatus(raw) {
  const value = toStringSafe(raw).toLowerCase();
  if (!value) return "Not Assigned";
  if (["yes", "done", "completed"].includes(value)) return "Completed";
  if (value.includes("partial")) return "Partially Done";
  if (["no", "not done", "n", "not completed"].includes(value)) return "Not Done";
  if (value.includes("not assigned")) return "Not Assigned";
  return "Not Assigned";
}

function safeSlug(value) {
  return toStringSafe(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

export function stableHash(input) {
  const raw = String(input || "");
  let hash = 0;
  for (let i = 0; i < raw.length; i += 1) {
    hash = (hash << 5) - hash + raw.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

export function buildLegacyLogDocFromCsvRow({
  row,
  studentLookup,
  tutorLookup,
  importBatchId,
}) {
  const dateBase = parseLegacyDate(row?.Date);
  const startMin = parseLegacyTimeToMinutes(row?.["Start Time"]);
  const endMin = parseLegacyTimeToMinutes(row?.["End Time"]);

  if (!dateBase || !Number.isFinite(startMin) || !Number.isFinite(endMin)) {
    return {
      skipReason: "Invalid date/time",
      docId: null,
      data: null,
    };
  }

  const sessionStart = applyMinutesToDate(dateBase, startMin);
  let sessionEnd = applyMinutesToDate(dateBase, endMin);
  if (sessionEnd <= sessionStart) {
    sessionEnd = new Date(sessionEnd.getTime() + 24 * 60 * 60 * 1000);
  }

  const studentName = normalizeText(row?.["Student Name"]);
  const tutorName = normalizeText(row?.["Tutor Name"]);

  const studentId = studentLookup.get(studentName.toLowerCase()) || null;
  const tutorUid = tutorLookup.get(tutorName.toLowerCase()) || null;

  const usedHoursRaw = numberOrNull(row?.["Used Hours"]);
  const usedHours = Number.isFinite(usedHoursRaw)
    ? usedHoursRaw
    : calculateRoundedUsedHours(sessionStart, sessionEnd);

  const ratings = {
    effort: toRating(row?.Effort),
    motivation: toRating(row?.Motivation),
    behavior: toRating(row?.Behavior),
    focus: toRating(row?.Focus),
    confidence: null,
  };

  const sessionDateKey = toDateKeyLocal(sessionStart);
  const uniqueKey = [
    sessionDateKey,
    studentName.toLowerCase(),
    tutorName.toLowerCase(),
    startMin,
    endMin,
  ].join("|");

  const docId = `legacy_${stableHash(uniqueKey)}_${safeSlug(studentName).slice(0, 18)}`;

  const data = {
    sessionId: null,
    sessionStart: toTimestampMaybe(sessionStart),
    sessionEnd: toTimestampMaybe(sessionEnd),
    sessionDateKey,

    tutorUid,
    tutorName,
    studentId,
    studentName,
    studentNameLower: studentName.toLowerCase(),
    subject: normalizeText(row?.Subject),
    shortAdminNote: "",

    usedHours,

    sessionType: "Legacy Import",
    topicCovered: "",

    homeworkStatus: normalizeLegacyHomeworkStatus(row?.["Homework Status"]),
    homeworkComments: normalizeParagraph(row?.["Homework Comments"]),

    materialUsed: "",
    questionsAttempted: null,
    questionsWrong: null,
    accuracyPercent: null,

    lessonActivity: normalizeParagraph(row?.["Lesson Activity"]),
    learningInsight: "",
    nextFocus: normalizeParagraph(row?.["Next Focus"]),
    homeworkGiven: "",

    ratings,
    studentFlag: inferStudentFlag({ ratings }),

    ai: {
      homeworkAssigned: "",
      sessionSummary: "",
      nextSessionPlan: "",
      riskAlert: "",
    },

    source: "legacy_csv_import",
    importedLegacy: true,
    legacyHash: stableHash(uniqueKey),
    importBatchId: toStringSafe(importBatchId),
    updatedAt: Timestamp.now(),
    updatedByUid: null,
    createdAt: Timestamp.now(),
    createdByUid: null,
  };

  return { skipReason: null, docId, data };
}
